import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, Tooltip as LeafletTooltip } from 'react-leaflet';

import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Box, Typography, Paper, CircularProgress, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Grid, Divider, Tooltip } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import api from '../../../../../services/api';

import Cookies from 'js-cookie';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import SyncIcon from '@mui/icons-material/Sync';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RoomIcon from '@mui/icons-material/Room';


// Custom Icons for different vehicle types (cache busted)
const createIcon = (url) => new L.Icon({
    iconUrl: url + '?v=2',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
});

const icons = {
    car: createIcon('https://cdn-icons-png.flaticon.com/512/3202/3202926.png'),
    moto: createIcon('https://cdn-icons-png.flaticon.com/512/3721/3721619.png'), // Motorcycle icon updated
    caminhao: createIcon('https://cdn-icons-png.flaticon.com/512/2766/2766155.png'),
    van: createIcon('https://cdn-icons-png.flaticon.com/512/2830/2830305.png'),
    default: createIcon('https://cdn-icons-png.flaticon.com/512/1067/1067357.png')
};

const iconInstalacao = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/619/619034.png',
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30]
});

const iconPanic = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/1022/1022319.png',
    iconSize: [45, 45],
    iconAnchor: [22, 45],
    popupAnchor: [0, -45],
    className: 'panic-marker-animation'
});

// Array of distinct cold, dark, strong colors for routes
const routeColors = [
    '#00008B', // DarkBlue
    '#4B0082', // Indigo
    '#800080', // Purple
    '#8B0000', // DarkRed
    '#006400', // DarkGreen
    '#2F4F4F', // DarkSlateGray
    '#191970', // MidnightBlue
    '#8B4513', // SaddleBrown
    '#800000', // Maroon
    '#483D8B'  // DarkSlateBlue
];

const getRondaColor = (id) => {
    return routeColors[(id || 0) % routeColors.length];
};

const panicStyle = `
  @keyframes panicPulse {
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.7); }
    70% { transform: scale(1.1); box-shadow: 0 0 0 15px rgba(211, 47, 47, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(211, 47, 47, 0); }
  }
  .panic-marker-animation {
    animation: panicPulse 1.5s infinite;
    background: rgba(211, 47, 47, 0.2);
    border-radius: 50%;
    border: 2px solid #d32f2f;
  }
`;

export default function MapaTempoReal({ darkMode, onOpenRonda, panicAlerts = [] }) {
    const [locations, setLocations] = useState([]);
    const [dashboard, setDashboard] = useState({ totalRondantes: 0, rondantesOnline: 0, rondasEmAndamento: 0 });
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [routeEtas, setRouteEtas] = useState({});
    const [selectedLoc, setSelectedLoc] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [kanbanOpen, setKanbanOpen] = useState(true);

    const allMarkers = useMemo(() => {
        const allLocs = [...locations];
        panicAlerts.forEach(panic => {
            const exists = allLocs.some(l => l.usuario?.id_usuario === panic.usuario?.id_usuario);
            if (!exists && panic.latitude && panic.longitude) {
                allLocs.push({
                    id_rastreamento: `panic-${panic.id_log_ronda_motorizada}`,
                    latitude: panic.latitude,
                    longitude: panic.longitude,
                    data_hora: panic.data_hora,
                    usuario: panic.usuario,
                    is_panic_only: true
                });
            }
        });
        return allLocs;
    }, [locations, panicAlerts]);

    const handleOpenModal = (loc) => {
        setSelectedLoc(loc);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const parseDate = (d) => {
        if (!d) return 'Desconhecido';
        if (Array.isArray(d)) {
            // [year, month, day, hour, minute, second]
            return new Date(d[0], d[1] - 1, d[2], d[3] || 0, d[4] || 0, d[5] || 0).toLocaleTimeString();
        }
        return new Date(d).toLocaleTimeString();
    };

    const parseDateFull = (d) => {
        if (!d) return 'Desconhecido';
        if (Array.isArray(d)) {
            return new Date(d[0], d[1] - 1, d[2], d[3] || 0, d[4] || 0, d[5] || 0).toLocaleString();
        }
        return new Date(d).toLocaleString();
    };

    const fetchLocations = async () => {
        try {
            const token = Cookies.get('token');
            const response = await api.get('/v1/protected/rastreamento/localizacoesativos', {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('[MapaTempoReal] Dados recebidos:', response.data);
            setLocations(response.data.locais || []);
            setDashboard(response.data);
            setLastUpdate(new Date());
            setLoading(false);
        } catch (error) {
            console.error('Erro ao buscar localizações:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLocations();
        const interval = setInterval(fetchLocations, 30000); // 30 segundos
        return () => clearInterval(interval);
    }, []);

    const getVehicleIcon = (loc) => {
        const tipo = loc.usuario?.veiculo?.tipo_veiculo;
        if (tipo === 0) return icons.car;
        if (tipo === 1) return icons.moto;
        if (tipo === 2) return icons.caminhao;
        if (tipo === 3) return icons.van;
        return icons.car; // default to car if unknown
    };

    if (loading && (!locations || locations.length === 0)) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper elevation={3} sx={{ p: 0, borderRadius: 4, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <style>{panicStyle}</style>
            <Box sx={{ p: 1.5, bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5', borderBottom: '1px solid', borderColor: darkMode ? '#333' : '#e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 3 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon fontSize="small" color="primary" />
                        Rondantes Online: {dashboard.rondantesOnline} / {dashboard.totalRondantes}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#e65100' }}>
                        <SecurityIcon fontSize="small" />
                        Rondas em Andamento: {dashboard.rondasEmAndamento}
                    </Typography>
                </Box>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                    <SyncIcon fontSize="small" />
                    Última atualização: {lastUpdate ? lastUpdate.toLocaleTimeString() : '...'}
                </Typography>
            </Box>
            
            <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                <MapContainer center={[-18.5122, -44.5550]} zoom={13} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                    url={darkMode 
                        ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    }
                    attribution='&copy; OpenStreetMap contributors &copy; CARTO'
                />
                
                
                    {allMarkers.map((loc) => {
                        const lat = parseFloat(loc.latitude);
                        const lng = parseFloat(loc.longitude);
                        if (isNaN(lat) || isNaN(lng)) return null;

                        const pos = [lat, lng];
                        const hasRonda = loc.rondaMotorizada;
                        
                        const historicalPoints = (hasRonda?.rastreamentosApp || [])
                            .map(p => [parseFloat(p.latitude), parseFloat(p.longitude)])
                            .filter(p => !isNaN(p[0]) && !isNaN(p[1]));

                        let destCoords = null;
                        if (hasRonda?.instalacao) {
                            const dLat = parseFloat(hasRonda.instalacao.latitude);
                            const dLng = parseFloat(hasRonda.instalacao.longitude);
                            if (!isNaN(dLat) && !isNaN(dLng)) {
                                destCoords = [dLat, dLng];
                            }
                        }

                        const isPanic = panicAlerts.some(p => p.usuario?.id_usuario === loc.usuario?.id_usuario);

                        return (
                            <React.Fragment key={loc.id_rastreamento}>
                                {/* Marker do Rondante */}
                                <Marker 
                                    position={pos} 
                                    icon={isPanic ? iconPanic : getVehicleIcon(loc)}
                                    eventHandlers={{
                                        click: () => handleOpenModal(loc)
                                    }}
                                    zIndexOffset={isPanic ? 2000 : 0}
                                >
                                    {(hasRonda || isPanic) && (
                                        <LeafletTooltip 
                                            direction="top" 
                                            offset={[0, -40]} 
                                            opacity={0.9} 
                                            permanent 
                                            interactive
                                        >
                                            <div 
                                                onClick={(e) => { e.stopPropagation(); handleOpenModal(loc); }}
                                                style={{ 
                                                    fontSize: '11px', 
                                                    textAlign: 'left', 
                                                    lineHeight: '1.4', 
                                                    color: darkMode ? '#fff' : '#000', 
                                                    minWidth: '150px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {isPanic && (
                                                    <div style={{ backgroundColor: '#d32f2f', color: '#fff', padding: '4px', borderRadius: '4px', marginBottom: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                                                        ⚠️ PÂNICO ACIONADO
                                                    </div>
                                                )}
                                                <strong>{loc.usuario?.nome_amigavel || 'Rondante'}</strong> {loc.usuario?.veiculo?.placa ? `(${loc.usuario.veiculo.placa})` : ''}<br/>
                                                {hasRonda && (
                                                    <>
                                                        <strong>Destino:</strong> {hasRonda.instalacao?.nome_fantasia || 'Desconhecido'}<br/>
                                                        <strong>Tempo Estimado:</strong> {routeEtas[hasRonda.id_ronda_motorizada]?.duration || 0} min<br/>
                                                        <strong>Distância:</strong> {routeEtas[hasRonda.id_ronda_motorizada]?.distance || 0} km<br/>
                                                    </>
                                                )}
                                                <span style={{ color: darkMode ? '#aaa' : '#555', fontSize: '10px' }}>Atualizado: {parseDateFull(loc.data_hora)}</span>
                                            </div>
                                        </LeafletTooltip>
                                    )}
                                </Marker>

                            {/* Trajeto já percorrido (Breadcrumb) */}
                            {historicalPoints.length > 1 && (
                                <Polyline 
                                    positions={historicalPoints} 
                                    color="#3b82f6" 
                                    weight={3} 
                                    opacity={0.6} 
                                    dashArray="5, 10"
                                />
                            )}

                            {/* Linha para o destino usando OSRM API */}
                            {destCoords && (
                                <>
                                    <OsrmRoute 
                                        start={pos} 
                                        end={destCoords} 
                                        color={getRondaColor(hasRonda.id_ronda_motorizada)} 
                                        onRouteData={(d) => {
                                            setRouteEtas(prev => ({ ...prev, [hasRonda.id_ronda_motorizada]: d }));
                                        }} 
                                    />
                                    <Marker position={destCoords} icon={iconInstalacao}>
                                        <Popup>Destino: {hasRonda.instalacao?.nome_fantasia}</Popup>
                                    </Marker>
                                </>
                            )}
                        </React.Fragment>
                    );
                })}
                
                
                <RecenterMap locations={locations} />
                </MapContainer>

                {/* KANBAN OVERLAY */}
                <Box sx={{ 
                    position: 'absolute', 
                    top: 10, 
                    right: 10, 
                    bottom: 10, 
                    width: kanbanOpen ? 320 : 60,
                    bgcolor: kanbanOpen ? (darkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)') : 'rgba(30, 30, 30, 0.4)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 4,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    boxShadow: 4,
                    overflow: 'hidden'
                }}>
                    <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: kanbanOpen ? 'space-between' : 'center', borderBottom: kanbanOpen ? '1px solid rgba(128,128,128,0.2)' : 'none' }}>
                        {kanbanOpen && (
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1, color: darkMode ? '#fff' : '#333' }}>
                                Rondas Ativas ({locations.filter(l => l.rondaMotorizada).length})
                            </Typography>
                        )}
                        <IconButton size="small" onClick={() => setKanbanOpen(!kanbanOpen)} sx={{ color: darkMode ? '#fff' : (kanbanOpen ? '#333' : '#fff') }}>
                            {kanbanOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', p: kanbanOpen ? 1.5 : 0.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {locations.filter(l => l.rondaMotorizada).map((loc) => (
                            <Paper key={loc.id_rastreamento} elevation={darkMode ? 2 : 1} sx={{ 
                                p: kanbanOpen ? 1.5 : 0.5, 
                                borderRadius: 3, 
                                bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                                borderLeft: '4px solid',
                                borderLeftColor: getRondaColor(loc.rondaMotorizada.id_ronda_motorizada),
                                transition: 'all 0.2s',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.02)' }
                            }} onClick={() => kanbanOpen ? null : handleOpenModal(loc)}>
                                {kanbanOpen ? (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: getRondaColor(loc.rondaMotorizada.id_ronda_motorizada), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                                                <DirectionsRunIcon fontSize="small" />
                                            </Box>
                                            <Box sx={{ overflow: 'hidden' }}>
                                                <Typography variant="caption" fontWeight="bold" display="block" noWrap>
                                                    {loc.usuario?.nome_amigavel}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {loc.usuario?.veiculo?.marca} {loc.usuario?.veiculo?.modelo}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 1, opacity: 0.5 }} />

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <RoomIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                                                <Typography variant="caption" sx={{ fontWeight: 500 }} noWrap>
                                                    {loc.rondaMotorizada.instalacao?.nome_fantasia || 'Instalação'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccessTimeIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                                <Typography variant="caption" color="text.secondary">
                                                    ETA: {routeEtas[loc.rondaMotorizada.id_ronda_motorizada]?.duration || 0} min
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Button 
                                            fullWidth 
                                            size="small" 
                                            variant="outlined" 
                                            sx={{ mt: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                                            onClick={(e) => { e.stopPropagation(); handleOpenModal(loc); }}
                                        >
                                            Ver Detalhes
                                        </Button>
                                    </>
                                ) : (
                                    <Tooltip title={loc.usuario?.nome_amigavel || 'Rondante'} placement="left">
                                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 0.5 }}>
                                            <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: getRondaColor(loc.rondaMotorizada.id_ronda_motorizada), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                                                    {loc.usuario?.nome_amigavel?.charAt(0) || 'R'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                )}
                            </Paper>
                        ))}
                        
                        {locations.filter(l => l.rondaMotorizada).length === 0 && kanbanOpen && (
                            <Box sx={{ py: 4, textAlign: 'center', opacity: 0.5 }}>
                                <Typography variant="caption">Nenhuma ronda em execução</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Modal Detalhes do Rondante */}
            <Dialog 
                open={modalOpen} 
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        maxHeight: '70vh',
                        bgcolor: darkMode ? '#1e1e1e' : '#fff',
                        color: darkMode ? '#fff' : 'inherit'
                    }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: darkMode ? '#333' : '#eee' }}>
                    <Typography variant="h6" fontWeight="bold">
                        Detalhes do Rondante
                    </Typography>
                    <IconButton onClick={handleCloseModal} sx={{ color: darkMode ? '#aaa' : '#666' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ p: 3 }}>
                    {selectedLoc && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                                    {selectedLoc.usuario?.veiculo?.tipo_veiculo === 1 ? <TwoWheelerIcon sx={{ color: '#fff' }} /> : <DirectionsCarIcon sx={{ color: '#fff' }} />}
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        {selectedLoc.usuario?.nome_amigavel || 'Rondante'}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {selectedLoc.usuario?.veiculo 
                                            ? `${selectedLoc.usuario.veiculo.marca} ${selectedLoc.usuario.veiculo.modelo} (${selectedLoc.usuario.veiculo.placa})` 
                                            : 'Veículo Não Atribuído'}
                                    </Typography>
                                </Box>
                            </Box>

                            {selectedLoc.rondaMotorizada ? (
                                <Box sx={{ bgcolor: darkMode ? 'rgba(33, 150, 243, 0.1)' : '#f5f9ff', p: 2, borderRadius: 3, mb: 3, border: '1px solid', borderColor: 'primary.main' }}>
                                    <Typography variant="caption" color="primary" fontWeight="bold" display="block" sx={{ mb: 1, letterSpacing: 1 }}>
                                        RONDA EM EXECUÇÃO
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Destino: {selectedLoc.rondaMotorizada.instalacao?.nome_fantasia || 'Instalação'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2, color: darkMode ? '#ccc' : '#666' }}>
                                        {selectedLoc.rondaMotorizada.instalacao?.logradouro}, {selectedLoc.rondaMotorizada.instalacao?.numero} - {selectedLoc.rondaMotorizada.instalacao?.bairro}<br/>
                                        {selectedLoc.rondaMotorizada.instalacao?.cidade} - {selectedLoc.rondaMotorizada.instalacao?.estado}
                                    </Typography>
                                    
                                    {routeEtas[selectedLoc.rondaMotorizada.id_ronda_motorizada] && (
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Box sx={{ flex: 1, bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : '#fff', p: 1.5, borderRadius: 2, textAlign: 'center' }}>
                                                <Typography variant="caption" display="block" color="textSecondary">Distância</Typography>
                                                <Typography variant="body1" fontWeight="bold">{routeEtas[selectedLoc.rondaMotorizada.id_ronda_motorizada].distance} km</Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : '#fff', p: 1.5, borderRadius: 2, textAlign: 'center' }}>
                                                <Typography variant="caption" display="block" color="textSecondary">Tempo</Typography>
                                                <Typography variant="body1" fontWeight="bold">{routeEtas[selectedLoc.rondaMotorizada.id_ronda_motorizada].duration} min</Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ p: 2, bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 3, mb: 3, textAlign: 'center' }}>
                                    <Chip label="Em patrulhamento livre" variant="outlined" />
                                </Box>
                            )}

                            <Box sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: darkMode ? '#333' : '#eee' }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                                    Informações de Rastreamento
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">Último Ping</Typography>
                                        <Typography variant="body2" fontWeight="medium">{parseDateFull(selectedLoc.data_hora)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">Dispositivo</Typography>
                                        <Typography variant="body2" fontWeight="medium">{selectedLoc.aparelho || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">Coordenadas</Typography>
                                        <Typography variant="body2" fontWeight="medium">{parseFloat(selectedLoc.latitude).toFixed(5)}, {parseFloat(selectedLoc.longitude).toFixed(5)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">Método</Typography>
                                        <Typography variant="body2" fontWeight="medium">{selectedLoc.metodo_conexao || 'App'}</Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Button  
                                fullWidth 
                                variant="contained" 
                                size="large" 
                                startIcon={<VisibilityIcon />}
                                onClick={() => {
                                    handleCloseModal();
                                    onOpenRonda(selectedLoc.rondaMotorizada ? selectedLoc.rondaMotorizada.id_ronda_motorizada : null);
                                }}
                                disabled={!selectedLoc.rondaMotorizada}
                                sx={{ mt: 3, borderRadius: 3, py: 1.5, textTransform: 'none', fontWeight: 'bold' }}
                            >
                                Ver Detalhes da Ronda
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Paper>

    );
}

function RecenterMap({ locations }) {
    const map = useMap();
    useEffect(() => {
        if (locations && locations.length > 0) {
            const validCoords = locations
                .map(loc => [parseFloat(loc.latitude), parseFloat(loc.longitude)])
                .filter(coords => !isNaN(coords[0]) && !isNaN(coords[1]));
            
            if (validCoords.length > 0) {
                const bounds = L.latLngBounds(validCoords);
                map.fitBounds(bounds, { padding: [100, 100], maxZoom: 15 });
            }
        }
    }, [locations.length === 0]);
    return null;
}

function OsrmRoute({ start, end, color, onRouteData }) {
    const [routePositions, setRoutePositions] = useState([]);
    const map = useMap();
    const [weight, setWeight] = useState(map.getZoom() < 12 ? 12 : map.getZoom() < 14 ? 9 : 6);

    useEffect(() => {
        const handleZoom = () => {
            const z = map.getZoom();
            setWeight(z < 12 ? 14 : z < 14 ? 10 : 7);
        };
        map.on('zoomend', handleZoom);
        return () => { map.off('zoomend', handleZoom); };
    }, [map]);

    useEffect(() => {
        if (!start || !end) return;
        let isMounted = true;
        const fetchRoute = async () => {
            try {
                // OSRM coordinates format: lon,lat
                const response = await fetch(`https://router.project-osrm.org/route/v1/driving/${start[1]},${start[0]};${end[1]},${end[0]}?overview=full&geometries=geojson`);
                const data = await response.json();
                if (data.routes && data.routes.length > 0) {
                    const route = data.routes[0];
                    const coords = route.geometry.coordinates.map(c => [c[1], c[0]]);
                    
                    if (isMounted) {
                        setRoutePositions(coords);
                        if (onRouteData) {
                            const distanceKm = (route.distance / 1000).toFixed(1);
                            const durationMin = Math.ceil(route.duration / 60);
                            onRouteData({ distance: distanceKm, duration: durationMin });
                        }
                    }
                }
            } catch (err) {
                console.error('Erro ao buscar rota OSRM', err);
            }
        };
        fetchRoute();
        return () => { isMounted = false; };
    }, [start[0], start[1], end[0], end[1]]);

    if (routePositions.length === 0) {
        // Fallback to straight line while loading or on error
        return <Polyline positions={[start, end]} color={color || "#ef4444"} weight={weight} opacity={0.6} />;
    }

    return (
        <Polyline 
            positions={routePositions} 
            color={color || "#ef4444"} 
            weight={weight} 
            opacity={0.9}
            dashArray="10, 10"
        />
    );
}
